import { Form, Input, Modal, Switch, Tooltip, notification } from 'antd';
import { useEffect, useState } from 'react';
import { NCAPIAccount } from '../api/models';
import APIClient from '../api/api';
import moment from 'moment';
import { ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import { Button, Column, DataTable, StatusTag } from '../components/ui';
import { PageHead, PageToolbar } from '../layout';

const NCAPIAccounts = () => {
  const [accounts, setAccounts] = useState<NCAPIAccount[]>();
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [updateTokenModalVisible, setUpdateTokenModalVisible] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<NCAPIAccount | null>(null);
  const [form] = Form.useForm();
  const [updateTokenForm] = Form.useForm();

  useEffect(() => {
    loadAccounts();
  }, []);

  async function loadAccounts() {
    try {
      setLoading(true);
      const resp = await APIClient.listNCAPIAccounts();
      setAccounts(resp.accounts);
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to load NC API accounts.' });
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateAccount(values: any) {
    try {
      await APIClient.createNCAPIAccount({
        username: values.username,
        exchange_token: values.exchange_token,
      });
      notification.success({ message: 'Account created successfully!' });
      setCreateModalVisible(false);
      form.resetFields();
      loadAccounts();
    } catch (e) {
      console.error(e);
      notification.error({
        message: 'Failed to create account. Please check the exchange token.',
      });
    }
  }

  async function handleUpdateToken(values: any) {
    if (!selectedAccount) return;
    try {
      await APIClient.updateNCAPIAccountToken(selectedAccount.id, {
        exchange_token: values.exchange_token,
      });
      notification.success({ message: 'Token updated successfully!' });
      setUpdateTokenModalVisible(false);
      updateTokenForm.resetFields();
      setSelectedAccount(null);
      loadAccounts();
    } catch (e) {
      console.error(e);
      notification.error({
        message: 'Failed to update token. Please check the exchange token.',
      });
    }
  }

  async function handleRefreshToken(account: NCAPIAccount) {
    try {
      await APIClient.refreshNCAPIAccountToken(account.id);
      notification.success({ message: 'Token refreshed successfully!' });
      loadAccounts();
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to refresh token.' });
    }
  }

  async function handleToggleStatus(account: NCAPIAccount) {
    try {
      await APIClient.updateNCAPIAccountStatus(account.id, {
        is_active: !account.is_active,
      });
      notification.success({
        message: `Account ${!account.is_active ? 'activated' : 'deactivated'} successfully!`,
      });
      loadAccounts();
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to update account status.' });
    }
  }

  function showUpdateTokenModal(account: NCAPIAccount) {
    setSelectedAccount(account);
    setUpdateTokenModalVisible(true);
  }

  const columns: Column<NCAPIAccount>[] = [
    {
      key: 'id',
      title: 'ID',
      width: 60,
      render: (a) => <span className="font-mono text-[12px] text-text-3 tabular-nums">#{a.id}</span>,
    },
    {
      key: 'username',
      title: 'Username',
      render: (a) => <span className="text-text font-medium text-[12.5px]">{a.username}</span>,
    },
    {
      key: 'status',
      title: 'Status',
      width: 100,
      render: (a) =>
        a.is_active ? (
          <StatusTag tone="success">active</StatusTag>
        ) : (
          <StatusTag tone="danger">inactive</StatusTag>
        ),
    },
    {
      key: 'token_expires_at',
      title: 'Token Expires',
      width: 160,
      render: (a) => {
        if (!a.token_expires_at) return <span className="text-text-3">—</span>;
        const expiresAt = moment(a.token_expires_at);
        const isExpired = expiresAt.isBefore(moment());
        const isExpiringSoon = expiresAt.isBefore(moment().add(1, 'hour'));
        const cls = isExpired
          ? 'text-danger'
          : isExpiringSoon
          ? 'text-warn'
          : 'text-text-2';
        return (
          <Tooltip title={expiresAt.format('LLL')}>
            <span className={`font-mono text-[12px] tabular-nums ${cls}`}>
              {isExpired ? 'expired' : expiresAt.fromNow()}
            </span>
          </Tooltip>
        );
      },
    },
    {
      key: 'last_token_refresh',
      title: 'Last Refresh',
      width: 160,
      render: (a) => (
        <span className="font-mono text-[12px] text-text-2 tabular-nums">
          {a.last_token_refresh ? moment(a.last_token_refresh).format('MMM D, HH:mm') : '—'}
        </span>
      ),
    },
    {
      key: 'created_at',
      title: 'Created',
      width: 140,
      render: (a) => (
        <span className="font-mono text-[12px] text-text-3 tabular-nums">
          {moment(a.created_at).format('MMM D, YYYY')}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      width: 280,
      render: (a) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            size="sm"
            variant="ghost"
            iconLeft={<ReloadOutlined />}
            onClick={() => showUpdateTokenModal(a)}
          >
            Update
          </Button>
          <Button
            size="sm"
            variant="ghost"
            iconLeft={<SyncOutlined />}
            onClick={() => handleRefreshToken(a)}
          >
            Refresh
          </Button>
          <Switch checked={a.is_active} onChange={() => handleToggleStatus(a)} size="small" />
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHead
        title="Namecheap API Accounts"
        subtitle="SSO-issued credentials for Namecheap API access."
        actions={
          <Button variant="primary" onClick={() => setCreateModalVisible(true)}>
            Create Account
          </Button>
        }
      />
      <PageToolbar>
        <span className="ml-auto text-[11px] text-text-3 font-mono">
          {accounts ? (
            <>
              total <b className="text-text-2">{accounts.length}</b>
            </>
          ) : (
            'loading…'
          )}
        </span>
      </PageToolbar>

      <DataTable
        columns={columns}
        data={accounts ?? []}
        rowKey={(a) => String(a.id)}
        loading={loading}
        emptyMessage="No NC API accounts."
      />

      <Modal
        title="Create NC API Account"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText="Create"
      >
        <Form form={form} onFinish={handleCreateAccount} layout="vertical">
          <Form.Item
            label="Username"
            name="username"
            rules={[{ required: true, message: 'Please enter username' }]}
          >
            <Input placeholder="Namecheap username" />
          </Form.Item>
          <Form.Item
            label="Exchange Token"
            name="exchange_token"
            rules={[{ required: true, message: 'Please enter exchange token' }]}
          >
            <Input.Password placeholder="SSO exchange token" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Update Token for ${selectedAccount?.username}`}
        open={updateTokenModalVisible}
        onCancel={() => {
          setUpdateTokenModalVisible(false);
          updateTokenForm.resetFields();
          setSelectedAccount(null);
        }}
        onOk={() => updateTokenForm.submit()}
        okText="Update"
      >
        <Form form={updateTokenForm} onFinish={handleUpdateToken} layout="vertical">
          <Form.Item
            label="New Exchange Token"
            name="exchange_token"
            rules={[{ required: true, message: 'Please enter new exchange token' }]}
          >
            <Input.Password placeholder="SSO exchange token" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default NCAPIAccounts;
